import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowLeft, Home } from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { ReferralForm } from '@/components/referral/ReferralForm';
import { ReferralSuccess } from '@/components/referral/ReferralSuccess';
import { TrustBadges } from '@/components/referral/TrustBadges';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { validateContractorCode, submitReferralLead } from '@/services/referralApi';

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

// Loading State Component
const LoadingState = () => (
  <div 
    className="max-w-lg mx-auto px-4 py-16 space-y-6"
    data-testid="referral-loading-state"
  >
    <div className="text-center space-y-4">
      <Skeleton className="h-4 w-32 mx-auto bg-white/20" />
      <Skeleton className="h-10 w-64 mx-auto bg-white/20" />
      <Skeleton className="h-6 w-48 mx-auto bg-white/20" />
    </div>
    <div className="rounded-2xl p-6 border border-white/15 bg-white/10 backdrop-blur-xl space-y-4">
      <Skeleton className="h-12 w-full bg-white/20" />
      <Skeleton className="h-12 w-full bg-white/20" />
      <Skeleton className="h-12 w-full bg-white/20" />
      <Skeleton className="h-14 w-full bg-white/30" />
    </div>
  </div>
);

// Invalid Code State Component
const InvalidCodeState = ({ message }) => (
  <motion.div
    variants={fadeUp}
    initial="hidden"
    animate="show"
    className="max-w-lg mx-auto px-4 py-16 text-center"
    data-testid="referral-invalid-state"
  >
    <div className="mx-auto w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mb-6">
      <AlertCircle className="h-10 w-10 text-red-400" />
    </div>
    
    <h1 
      className="font-['Playfair_Display'] text-3xl text-white mb-4"
      data-testid="invalid-code-headline"
    >
      Invalid Referral Link
    </h1>
    
    <p 
      className="text-white/70 mb-8 max-w-sm mx-auto"
      data-testid="invalid-code-message"
    >
      {message || "This referral link is no longer valid or has expired."}
    </p>
    
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button
        asChild
        variant="outline"
        className="h-12 px-6 rounded-xl border-white/30 text-white hover:bg-white/10"
        data-testid="invalid-code-back-btn"
      >
        <Link data-testid="referral-landing-link-2" to="/">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Link>
      </Button>
      
      <Button
        asChild
        className="h-12 px-6 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold"
        data-testid="invalid-code-home-btn"
      >
        <Link data-testid="referral-landing-link-1" to="/">
          <Home className="h-4 w-4 mr-2" />
          Visit KGOB Home
        </Link>
      </Button>
    </div>
  </motion.div>
);

export default function ReferralLanding() {
  const { code } = useParams();
  
  // Page state: loading | invalid_code | ready | submitting | success
  const [pageStatus, setPageStatus] = useState('loading');
  const [contractorName, setContractorName] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  // Validate contractor code on mount
  useEffect(() => {
    const validateCode = async () => {
      if (!code) {
        setErrorMessage('No referral code provided');
        setPageStatus('invalid_code');
        return;
      }

      try {
        const result = await validateContractorCode(code);
        
        if (result.valid) {
          setContractorName(result.contractor_name);
          setPageStatus('ready');
        } else {
          setErrorMessage(result.message);
          setPageStatus('invalid_code');
        }
      } catch (error) {
        console.error('Code validation error:', error);
        setErrorMessage('Unable to verify referral link. Please try again.');
        setPageStatus('invalid_code');
      }
    };

    validateCode();
  }, [code]);

  // Handle form submission
  const handleSubmit = async (formData) => {
    setPageStatus('submitting');
    
    try {
      const result = await submitReferralLead({
        contractor_code: code,
        ...formData
      });

      if (result.success) {
        setSubmitResult(result);
        setPageStatus('success');
        toast.success('Thank you! We\'ll be in touch soon.');
      } else {
        setPageStatus('ready');
        toast.error(result.message || 'Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setPageStatus('ready');
      toast.error('An error occurred. Please try again.');
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      data-testid="referral-landing-page"
    >
      <Helmet>
        <title>Referred by a Trusted Partner | KGOB CPAs & Advisors</title>
        <meta name="description" content="You've been referred to KGOB CPAs & Advisors by a trusted partner. Get expert tax preparation, planning, and financial advisory services." />
      </Helmet>
      {/* Header */}
      <Header />

      {/* Main Content */}
      <main className="flex-1 bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] noise-overlay">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          
          {/* Loading State */}
          {pageStatus === 'loading' && <LoadingState />}

          {/* Invalid Code State */}
          {pageStatus === 'invalid_code' && (
            <InvalidCodeState message={errorMessage} />
          )}

          {/* Ready State - Show Form */}
          {(pageStatus === 'ready' || pageStatus === 'submitting') && (
            <div className="max-w-lg mx-auto">
              {/* Hero Section */}
              <motion.div
                variants={fadeUp}
                initial="hidden"
                animate="show"
                className="text-center mb-8"
              >
                <p 
                  className="uppercase tracking-[0.18em] text-xs text-[#E3C96E] mb-4"
                  data-testid="referral-tagline"
                >
                  REFERRED BY A TRUSTED PARTNER
                </p>
                <h1 
                  className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight"
                  data-testid="referral-headline"
                >
                  Professional Tax & Financial Services
                </h1>
                <p 
                  className="text-white/70 text-lg max-w-md mx-auto"
                  data-testid="referral-subheadline"
                >
                  You've been referred by a trusted KGOB partner. Let us help you achieve financial clarity.
                </p>
              </motion.div>

              {/* Form */}
              <ReferralForm data-testid="referral-landing-referralform-1"
                onSubmit={handleSubmit}
                isSubmitting={pageStatus === 'submitting'}
                contractorName={contractorName}
              />

              {/* Trust Badges */}
              <TrustBadges />
            </div>
          )}

          {/* Success State */}
          {pageStatus === 'success' && (
            <div className="max-w-lg mx-auto">
              <ReferralSuccess
                leadId={submitResult?.lead_id}
                warning={submitResult?.warning}
                onboardUrl={submitResult?.onboard_url}
                redirectUrl={submitResult?.redirect_url}
              />
              <TrustBadges />
            </div>
          )}

        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
